import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

export const AdminLessons = () => {
  const [cohort, setCohort] = useState("cosmic");
  const [lessons, setLessons] = useState([]);
  const [form, setForm] = useState({ title: "", body: "" });
  const load = () => api.get(`/courses/${cohort}/lessons`).then((r) => setLessons(r.data));
  useEffect(() => {
    load();
  }, [cohort]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/lessons", { ...form, cohort, order: lessons.length + 1 });
      setForm({ title: "", body: "" });
      toast.success("Lesson added to the classroom.");
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };
  const remove = async (id) => {
    await api.delete(`/admin/lessons/${id}`);
    load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="admin-lessons">
      <form onSubmit={add} className="card-soft p-8 space-y-4">
        <select className="field" value={cohort} onChange={(e) => setCohort(e.target.value)} data-testid="lesson-cohort-select">
          <option value="cosmic">The Cosmic Business Blueprint</option>
          <option value="apprenticeship">Holistic Home Care Practitioner Apprenticeship</option>
        </select>
        <input required placeholder="Lesson title" className="field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lesson-title-input" />
        <textarea required rows={8} placeholder="Lesson content" className="field" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="lesson-body-input" />
        <button className="btn btn-olive" data-testid="lesson-save-btn">Add Lesson</button>
      </form>
      <ul className="space-y-3">
        {lessons.length === 0 && <p className="text-[#232b1b]/60">No lessons in this cohort yet.</p>}
        {lessons.map((l, i) => (
          <li key={l.id} className="card-soft p-5 flex justify-between gap-4" data-testid={`admin-lesson-${l.id}`}>
            <span>Lesson {i + 1}: {l.title}</span>
            <button onClick={() => remove(l.id)} className="text-sm text-[#9c3d5c] hover:underline" data-testid={`admin-lesson-delete-${l.id}`}>Delete</button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AdminDiscounts = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", code: "", details: "" });
  const load = () => api.get("/discounts").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/discounts", form);
      setForm({ title: "", code: "", details: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };
  const remove = async (id) => {
    await api.delete(`/admin/discounts/${id}`);
    load();
  };
  return (
    <div className="space-y-8">
    <StreakCode />
    <div className="grid lg:grid-cols-2 gap-8" data-testid="admin-discounts">
      <form onSubmit={add} className="card-soft p-8 space-y-4">
        <input required placeholder="Title (e.g. Clinic consultations)" className="field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="discount-title-input" />
        <input required placeholder="Code" className="field font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="discount-code-input" />
        <textarea rows={3} placeholder="Details" className="field" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="discount-details-input" />
        <button className="btn btn-olive" data-testid="discount-save-btn">Add Discount</button>
      </form>
      <ul className="space-y-3">
        {items.map((d) => (
          <li key={d.id} className="card-soft p-5 flex justify-between gap-4" data-testid={`admin-discount-${d.id}`}>
            <span><span className="font-medium">{d.title}</span> <span className="font-mono text-[#b6689a] ml-2">{d.code}</span></span>
            <button onClick={() => remove(d.id)} className="text-sm text-[#9c3d5c] hover:underline" data-testid={`admin-discount-delete-${d.id}`}>Delete</button>
          </li>
        ))}
      </ul>
    </div>
    </div>
  );
};

const StreakCode = () => {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  useEffect(() => {
    api.get("/admin/streak-code").then((r) => { setData(r.data); setCode(r.data.code); });
  }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/streak-code", { code });
      toast.success("The new gratitude code is set for the next round.");
    } catch (err) {
      toast.error(formatErr(err));
    }
  };
  if (!data) return null;
  return (
    <div className="card-soft p-8" data-testid="admin-streak-code">
      <p className="eyebrow">4-Week Gratitude Reward</p>
      <p className="text-sm text-[#232b1b]/70 mt-3 max-w-2xl">Members who fill out their accountability scoreboard 4 weeks in a row receive 30% off at The Healers Market. Every Saturday the site sends this code to each new winner in their private Messages from you. Change it here for each new round.</p>
      <form onSubmit={save} className="flex flex-wrap gap-3 mt-5">
        <input required minLength={2} className="field font-mono max-w-sm" value={code} onChange={(e) => setCode(e.target.value)} data-testid="streak-code-input" />
        <button className="btn btn-olive" data-testid="streak-code-save-btn">Save Code</button>
      </form>
      {data.rewards.length > 0 && (
        <ul className="mt-6 text-sm space-y-1" data-testid="streak-rewards-list">
          {data.rewards.map((r, i) => <li key={i}>{r.name} · week of {r.week_start} · <span className="font-mono text-[#b6689a]">{r.code}</span></li>)}
        </ul>
      )}
    </div>
  );
};
